import {useMemo, useState} from 'react';
import {Formik} from 'formik';
import {useSelector} from 'react-redux';
import {Box, Drawer, Tooltip, Typography} from '@mui/material';
import Button from '@components/Button';
import PaymentOptions from '@/components/Organization/Auction/AddTableTicket/PaymentTicketsInfo/PaymentOptions';
import {COVER_FEE_TEXT, H6, TOP} from '@/utils/constants';
import {IRaffleTicketsData} from '@/utils/types';

import {ReactComponent as QuestionIcon} from '@icons/question.svg';
import RaffleTicketItem from '../Raffle/RaffleTicketItem';
import {styles} from './RaffleBuyTicketPage.styles';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getRaffleListSelector} from '@/store/raffle/selectors';
import {useNavigate} from 'react-router-dom';
import {getUserSelector} from '@/store/user';
import {toast} from 'react-toastify';
import Checkbox from '@/shared/components/Checkbox';
import {currencyFormatter} from '@/utils/helpers/currency';
import {calculateFees} from '@/utils/helpers/feeCalculations';
import {RaffleService} from '@/utils/services/raffle';
import {getErrorMessage} from '@/utils/helpers';

const UserRaffleBuyTicketPage = () => {
  const navigate = useNavigate();

  const user = useAppSelector(getUserSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const raffleTicketsData = useSelector(getRaffleListSelector);

  const [isCoverFee, setIsCoverFee] = useState(true);

  const totalPrice = useMemo(() => {
    return raffleTicketsData.reduce((sum: number, tier: IRaffleTicketsData) => {
      return sum + tier.amount * tier.price;
    }, 0);
  }, [raffleTicketsData]);

  const handleConfirm = async () => {
    if (!currentEvent?.id || !user?.bidderId) {
      toast.error('Missing required user or event data');
      return;
    }

    const selectedBundle = raffleTicketsData.find((item: IRaffleTicketsData) => item.amount > 0);

    if (!selectedBundle) {
      toast.error('Please select at least one raffle ticket bundle');
      return;
    }
    try {
      const reqBody = {
        attendeeId: user.bidderId,
        raffleBundleId: selectedBundle.id!,
        amount: selectedBundle.amount,
        isCoverFee,
      };

      const {data} = await RaffleService.buy({eventId: currentEvent.id, reqBody});
      if (data?.url) {
        window.location.href = data.url;
      }
      navigate(`/${currentEvent?.customizeWebpage}/raffle`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fees = useMemo(
    () => calculateFees(+totalPrice, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee ?? 0),
    [totalPrice, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee]
  );

  const handlePaymentSubmit = () => {
    // TODO call backend endpoint
  };
  return (
    <Box sx={styles.content}>
      <Box sx={styles.tiersWrap}>
        <Typography variant={H6} sx={styles.title}>
          Buy Raffle Tickets
        </Typography>

        <Box sx={styles.contentWrap}>
          <Typography sx={styles.listTitle}>Raffle tickets:</Typography>
          <Box sx={styles.tableHead}>
            <Typography sx={styles.columnLabel}>Price</Typography>
            <Typography sx={styles.columnLabel}>Amount</Typography>
            <Typography sx={styles.columnLabel}>Subtotal</Typography>
          </Box>
          {raffleTicketsData.map((tier: IRaffleTicketsData) => (
            <Box key={tier.price} sx={styles.ticketRow}>
              <RaffleTicketItem tier={tier} raffleData={raffleTicketsData} />
            </Box>
          ))}
          <Box sx={styles.feeWrap}>
            <Box sx={styles.feeLabel}>
              <Checkbox value={isCoverFee} onChange={() => setIsCoverFee(!isCoverFee)} />
              <Typography>Cover fee</Typography>
              <Tooltip title={`${COVER_FEE_TEXT} info`} placement={TOP}>
                <Box sx={styles.coverFeeTooltip}>
                  <QuestionIcon />
                </Box>
              </Tooltip>
            </Box>
            <Typography>
              {isCoverFee ? currencyFormatter.format(fees.coverFeeLine) : currencyFormatter.format(0)}
            </Typography>
          </Box>
          <Box sx={styles.totalWrap}>
            <Typography sx={styles.totalText}>Total</Typography>
            <Typography sx={styles.totalText}>${totalPrice}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.paymentWrap}>
        <Formik initialValues={{}} onSubmit={handlePaymentSubmit}>
          <PaymentOptions />
        </Formik>
      </Box>

      <Drawer variant="permanent" anchor="bottom" sx={styles.drawer}>
        <Box sx={styles.popup}>
          <Button
            variant="outline"
            onClick={() => navigate(`/${currentEvent?.customizeWebpage}/raffle`)}
            title="Cancel"
          />
          <Button onClick={handleConfirm} title="Buy" />
        </Box>
      </Drawer>
    </Box>
  );
};

export default UserRaffleBuyTicketPage;
